import React from 'react';
import { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { addDays, subDays, format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import PlotViewer from './PlotViewer';
import ProfilesViewer from './ProfilesViewer';
import TimeseriesViewer from './TimeseriesViewer';
import L1PlotViewer from './L1PlotViewer';
import DailyRecapsViewer from './DailyRecapsViewer';
import AvailableDataViewer from './AvailableDataViewer';
import LoginForm from './LoginForm';
import './App.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const PUBLIC_CAMPAIGNS = ['Andoya', 'Jungfraujoch', 'Payerne']

function App() {
  const [activeTab, setActiveTab] = useState('plot')
  const [selectedDateBoth, setSelectedDateBoth] = useState(new Date()); // Date for both tabs
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);
  const [availableDates, setAvailableDates] = useState(new Set());
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const isPublicCampaign = (campaign) => {
    return PUBLIC_CAMPAIGNS.includes(campaign);
  };

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Check authentication status on app load
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/auth-status`, {
          credentials: 'include' // Include cookies for session
        });
        if (response.ok) {
          const data = await response.json();
          setIsAuthenticated(data.authenticated);
        }
      } catch (err) {
        console.error('Error checking auth status:', err);
        setIsAuthenticated(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };
    
    checkAuthStatus();
  }, []);
  
  // Login function
  const handleLogin = async (password) => {
    setIsLoggingIn(true);
    setLoginError('');
    
    try {
      const response = await fetch(`${API_BASE}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for session
        body: JSON.stringify({ password })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsAuthenticated(true);
        setLoginError('');
        setShowLogin(false);
      } else {
        setLoginError(data.message || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      setLoginError('Network error. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };
  
  // Logout function
  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/api/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setIsAuthenticated(false);
    } catch (err) {
      console.error('Logout error:', err);
      // Force logout on client side even if server request fails
      setIsAuthenticated(false);
    }
  };

  // Fetch available dates for a campaign
  const fetchAvailableDates = async (campaign) => {
    if (!campaign) {
      setAvailableDates(new Set());
      return;
    }

    setIsLoadingDates(true);
    try {
      const response = await fetch(`${API_BASE}/api/available-dates/${campaign}`, {
        credentials: 'include' // Include session cookie
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableDates(new Set(data.dates));
        console.log(`Loaded ${data.dates.length} available dates for ${campaign}`);
      } else {
        console.error('Failed to fetch available dates');
        setAvailableDates(new Set());
      }
    } catch (err) {
      console.error('Error fetching available dates:', err);
      setAvailableDates(new Set());
    } finally {
      setIsLoadingDates(false);
    }
  };

  // Fetch available campaign sites when authenticated
  useEffect(() => {
    // if (!isAuthenticated) return;
    
    fetch(`${API_BASE}/api/campaigns`, {
      credentials: 'include' // Include session cookie
    })
      .then(res => res.ok ? res.json() : Promise.reject('Failed to fetch campaigns'))
      .then(data => {
        setAvailableCampaigns(data.campaigns);
        setSelectedCampaign(prev => (prev || data.campaigns[0] || ''));
        setIsLoadingCampaigns(false);
      })
      .catch(err => {
        console.error('Error fetching campaigns:', err);
        setIsLoadingCampaigns(false);
      });
  }, [isAuthenticated]);

  // Fetch available dates when campaign changes
  useEffect(() => {
    // if (isAuthenticated) {
    //   fetchAvailableDates(selectedCampaign);
    // }
    if (selectedCampaign && (isAuthenticated || isPublicCampaign(selectedCampaign))) {
      fetchAvailableDates(selectedCampaign);
    }
  }, [selectedCampaign, isAuthenticated]);

  // Prevent unauthenticated users from staying on L1 tab.
  useEffect(() => {
    if (!isAuthenticated && (activeTab === 'l1quicklook' || activeTab === 'recaps' || activeTab === 'availabledata')) {
      setActiveTab('plot');
    }
  }, [isAuthenticated, activeTab]);

  // Function to determine day class name for styling
  const getDayClassName = (date) => {
    if (!selectedCampaign) return undefined;
    
    const formattedDate = format(date, 'yyyy-MM-dd');
    return availableDates.has(formattedDate) ? undefined : 'react-datepicker__day--unavailable';
  };

  // Show loading spinner while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="App">
        <div className="loading-state" style={{ minHeight: '100vh' }}>
          <span className="loading-spinner"></span>
          Loading...
        </div>
      </div>
    );
  }
  
  // Show login form if not authenticated
  // if (!isAuthenticated) {
  //   return (
  //     <LoginForm 
  //       onLogin={handleLogin} 
  //       isLoading={isLoggingIn}
  //       error={loginError}
  //     />
  //   );
  // }
  const needsLogin =
    !isAuthenticated &&
    selectedCampaign &&
    !isPublicCampaign(selectedCampaign);

  // if (!isAuthenticated && selectedCampaign && !isPublicCampaign(selectedCampaign) ) {
  //   return (
  //     <LoginForm 
  //       onLogin={handleLogin} 
  //       isLoading={isLoggingIn}
  //       error={loginError}
  //     />
  //   );  
  // }

  return (
    <div className="App">
      {/* Header */}
      <header className="app-header">
        <div className="header-container">
          <div className="header-brand">
            <img className="logo" src="/logo_euliaa.png" alt="EULIAA logo"/>
            <h1 className="header-title">EULIAA Data Visualization</h1>
          </div>
          <div className="header-actions">
            {activeTab === 'recaps' ? (
              <button
                className="btn btn-primary"
                onClick={() => setActiveTab('plot')}
                title="Back to data visualisation"
              >
                View Data
              </button>
            ) : (
              <button
                className={`btn ${isAuthenticated ? 'btn-outline' : 'btn-outline'}`}
                onClick={() => {
                  if (!isAuthenticated) {
                    setShowLogin(true);
                    return;
                  }
                  setActiveTab('recaps');
                }}
                disabled={!isAuthenticated}
                title={!isAuthenticated ? 'Login required' : 'View daily file transfer recaps for all campaigns'}
              >
                Daily Recaps
              </button>
            )}
            {isAuthenticated ? (
              <button
                className="btn btn-danger"
                onClick={handleLogout}
                title="Logout"
              >
                Logout
              </button>
            ) : (
              <button
                className="btn btn-success"
                onClick={() => setShowLogin(true)}
                title="Login to access restricted campaigns, L1 quicklooks, and daily recaps"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Intro Section */}
      {activeTab !== 'recaps' && (
        <section className="intro-section">
          <div className="intro-card">
            <h3>Welcome to the EULIAA lidar campaign data visualization portal</h3>
            <p>
              More info on the EULIAA project: <a href="https://euliaa.eu/" target="_blank" rel="noopener noreferrer">euliaa.eu</a>
            </p>
            <p>
              Campaign data, including measurements and intercomparison data (e.g., radio sounding, model runs), 
              can be downloaded from the <a href="https://gkhub.earthobservations.org/packages/6h0dc-3rz24" target="_blank" rel="noopener noreferrer">GEO Knowledge Hub</a>
            </p>
            <p>
              The EULIAA wind data is also shared in real-time with operational weather services through <a href="https://e-profile.eu/" target="_blank" rel="noopener noreferrer">E-Profile</a>
            </p>
          </div>
        </section>
      )}

      {/* {!isAuthenticated && (
        <div style={{padding: '20px', textAlign:'left'}}>
          <span style={{ marginRight: '10px' }}>To access more campaigns:</span>
          <button 
          onClick={() => setShowLogin(true)}
          style={{
                padding: '8px 16px',
                backgroundColor: '#90dd66d3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
          title="Login"
          >
            Login
          </button>
        </div>
      )} */}

      {/* Controls Bar */}
      {activeTab !== 'recaps' && (
        <div className="controls-bar">
          <div className="controls-container">
            <div className="control-group">
              <label className="control-label">Campaign</label>
              {isLoadingCampaigns ? (
                <span className="loading-indicator">
                  <span className="loading-spinner"></span>
                  Loading...
                </span>
              ) : (
                <select
                  className="modern-select"
                  value={selectedCampaign}
                  onChange={(e) => setSelectedCampaign(e.target.value)}
                  title={!isAuthenticated ? 'Login required to access restricted campaigns' : undefined}
                >
                  <option value="">Select campaign</option>
                  {availableCampaigns.map(campaign => (
                    <option key={campaign} value={campaign}>
                      {campaign} {!isPublicCampaign(campaign) ? '🔒' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="control-group">
              <label className="control-label">Date</label>
              <div className="date-nav">
                <button
                  className="date-nav-btn"
                  onClick={() => setSelectedDateBoth(prev => subDays(prev, 1))}
                  title="Previous day"
                >
                  ←
                </button>

                <DatePicker
                  selected={selectedDateBoth}
                  onChange={(date) => setSelectedDateBoth(date)}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Choose a date"
                  popperPlacement="bottom-start"
                  dayClassName={getDayClassName}
                  disabled={isLoadingDates}
                />

                <button
                  className="date-nav-btn"
                  onClick={() => setSelectedDateBoth(prev => addDays(prev, 1))}
                  title="Next day"
                >
                  →
                </button>

                {isLoadingDates && (
                  <span className="loading-indicator">
                    <span className="loading-spinner"></span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      


      {/* Tab Navigation */}
      {activeTab !== 'recaps' && (
        <nav className="tab-nav">
          <div className="tab-nav-inner">
            <button
              className={`tab-btn ${activeTab === 'plot' ? 'active' : ''}`}
              onClick={() => setActiveTab('plot')}
            >
              Quicklooks
            </button>

            <button
              className={`tab-btn ${activeTab === 'profiles' ? 'active' : ''}`}
              onClick={() => setActiveTab('profiles')}
            >
              Profile Comparisons
            </button>

            <button
              className={`tab-btn ${activeTab === 'timeseries' ? 'active' : ''}`}
              onClick={() => setActiveTab('timeseries')}
            >
              Timeseries Comparisons
            </button>

            <button
              className={`tab-btn ${activeTab === 'l1quicklook' ? 'active' : ''} ${!isAuthenticated ? 'disabled' : ''}`}
              onClick={() => {
                if (!isAuthenticated) {
                  setShowLogin(true);
                  return;
                }
                setActiveTab('l1quicklook');
              }}
              title={!isAuthenticated ? 'Login required' : undefined}
            >
              L1 Quicklooks {!isAuthenticated && '🔒'}
            </button>

            <button
              className={`tab-btn ${activeTab === 'availabledata' ? 'active' : ''} ${!isAuthenticated ? 'disabled' : ''}`}
              onClick={() => {
                if (!isAuthenticated) {
                  setShowLogin(true);
                  return;
                }
                setActiveTab('availabledata');
              }}
              title={!isAuthenticated ? 'Login required' : undefined}
            >
              Available Data {!isAuthenticated && '🔒'}
            </button>

          </div>
        </nav>
      )}
    

      {/* Main Content */}
      <main className="main-content">
        {activeTab === 'plot' && <PlotViewer selectedDate={selectedDateBoth} selectedCampaign={selectedCampaign} />}
        {activeTab === 'l1quicklook' && <L1PlotViewer selectedDate={selectedDateBoth} selectedCampaign={selectedCampaign} />}
        {activeTab === 'recaps' && <DailyRecapsViewer />}
        {activeTab === 'availabledata' && <AvailableDataViewer selectedDate={selectedDateBoth} selectedCampaign={selectedCampaign} />}
        {activeTab === 'profiles' && <ProfilesViewer selectedDateProfile={selectedDateBoth} selectedCampaign={selectedCampaign} />}
        {activeTab === 'timeseries' && <TimeseriesViewer selectedDate={selectedDateBoth} selectedCampaign={selectedCampaign} />}
      </main>


      {(needsLogin || showLogin) && (
        <div className="login-overlay">
          <LoginForm 
            onLogin={handleLogin}
            isLoading={isLoggingIn}
            error={loginError}
          />
          {/* <p style={{ color: 'white', marginTop: '10px', textAlign: 'center' }}>
            This campaign requires authentication
          </p> */}
        </div>
      )}
    </div>
  );

}

export default App;
